'use client';

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getSupabase, currentUser, signOut } from "@/lib/supabase";
import { loadRecentTraining } from "@/lib/training";
import { loadWeightData } from "@/lib/weight";
import { loadWeeklyMacros } from "@/lib/macros";
import { useModals } from "@/context/ModalContext";
import DashboardLoader from "@/components/DashboardLoader";
import ProductTour from "@/components/ProductTour";
import { ReferralPanelCompact } from "@/components/ReferralPanel"; // ← NEW IMPORT
import Sidebar from "@/components/Sidebar";

import {
  Home, Apple, Dumbbell, Trophy, Settings, LogOut,
  Calendar, CalendarDays, Utensils
} from 'lucide-react';

import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';

// ——————————————————————————————————
// MACRO CARD WITH PROGRESS RING
// ——————————————————————————————————
function MacroCard({ title, current, goal, dataKey, weekData, loadingMacros }: {
  title: string;
  current: string;
  goal: string;
  dataKey: 'cal' | 'pro' | 'carb' | 'fat';
  weekData: any[];
  loadingMacros: boolean;
}) {
  const currentNum = parseFloat(current) || 0;
  const goalNum = parseFloat(goal) || 1;
  const percentage = Math.min((currentNum / goalNum) * 100, 100);
  
  const getStatus = () => {
    if (percentage >= 95 && percentage <= 105) return { text: 'On Track', color: 'text-green-400', bgColor: 'bg-green-500/20' };
    if (percentage > 105) return { text: 'Ahead', color: 'text-blue-400', bgColor: 'bg-blue-500/20' };
    return { text: 'Behind', color: 'text-orange-400', bgColor: 'bg-orange-500/20' };
  };

  const status = getStatus();
  
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const [hoveredPoint, setHoveredPoint] = useState<{ day: string; value: number } | null>(null);

  return (
    <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-3 md:p-5 flex flex-col h-full hover:border-green-500/30 transition-colors group">
      <div className="flex justify-between items-center mb-2 md:mb-3">
        <h3 className="text-xs md:text-sm text-gray-500 font-medium">{title}</h3>
        <span className={`text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full font-medium ${status.bgColor} ${status.color}`}>
          {status.text}
        </span>
      </div>
      
      <div className="flex items-center gap-2 md:gap-4 mb-2 md:mb-4">
        {/* Progress Ring - smaller on mobile */}
        <div className="relative w-14 h-14 md:w-20 md:h-20 flex-shrink-0">
          <svg className="transform -rotate-90 w-14 h-14 md:w-20 md:h-20">
            <circle
              cx="50%"
              cy="50%"
              r="45%"
              stroke="currentColor"
              strokeWidth="5"
              fill="none"
              className="text-gray-800"
            />
            <circle
              cx="50%"
              cy="50%"
              r="45%"
              stroke="currentColor"
              strokeWidth="5"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="text-green-400 transition-all duration-500 ease-out"
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm md:text-lg font-bold text-[#0f172a]">{Math.round(percentage)}%</span>
          </div>
        </div>

        {/* Numbers */}
        <div className="flex-1 min-w-0">
          <p className="text-xl md:text-3xl font-bold text-[#0f172a] leading-tight truncate">
            {current}
          </p>
          <p className="text-xs md:text-sm text-gray-500">
            of {goal}
          </p>
        </div>
      </div>

      {/* Interactive Chart - hidden on very small screens */}
      <div className="flex-1 items-center justify-center min-h-[60px] md:min-h-[100px] relative hidden sm:flex">
        {loadingMacros ? (
          <p className="text-gray-500 text-sm">Loading...</p>
        ) : weekData.length === 0 ? (
          <p className="text-gray-500 text-sm">No data</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={weekData} 
                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                onMouseMove={(e: any) => {
                  if (e.activePayload) {
                    setHoveredPoint({
                      day: e.activePayload[0].payload.day,
                      value: e.activePayload[0].value
                    });
                  }
                }}
                onMouseLeave={() => setHoveredPoint(null)}
              >
                <CartesianGrid stroke="#475569" vertical={false} />
                <Line 
                  type="monotone" 
                  dataKey={dataKey} 
                  stroke="#a78bfa" 
                  strokeWidth={2} 
                  dot={{ fill: '#a78bfa', r: 3 }}
                  activeDot={{ r: 5, fill: '#a78bfa', strokeWidth: 2, stroke: '#0f172a' }}
                />
                <XAxis 
                  dataKey="day" 
                  tick={{ fill: '#545f6e', fontSize: 11 }} 
                  axisLine={false} 
                  tickLine={false} 
                />
                <YAxis hide />
                <Tooltip 
                  contentStyle={{ 
                    background: '#f8fafc', 
                    border: '1px solid #a78bfa', 
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(167,139,250, 0.2)'
                  }} 
                  labelStyle={{ color: '#545f6e', fontWeight: 'bold' }}
                  formatter={(value: any) => [`${value}`, title]}
                />
              </LineChart>
            </ResponsiveContainer>
            
            {hoveredPoint && (
              <div className="absolute top-2 left-2 bg-black/80 backdrop-blur-sm border border-green-500/50 rounded-lg px-3 py-2 pointer-events-none">
                <p className="text-xs text-gray-500">{hoveredPoint.day}</p>
                <p className="text-sm font-bold text-green-400">{hoveredPoint.value}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ——————————————————————————————————
// MAIN DASHBOARD WITH LOADER & TOUR
// ——————————————————————————————————
export default function Dashboard() {
  const { openAddActivity, openLogFood, openUpdateGoal } = useModals();

  const [isLoading, setIsLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showTour, setShowTour] = useState(false);

  // Core dashboard state
  const [trainingData, setTrainingData] = useState<any[]>([]);
  const [loadingTraining, setLoadingTraining] = useState(true);
  const [weekData, setWeekData] = useState<any[]>([]);
  const [todayGoals, setTodayGoals] = useState<{ cal: number; pro: number; carb: number; fat: number }>({ cal: 0, pro: 0, carb: 0, fat: 0 });
  const [loadingMacros, setLoadingMacros] = useState(true);
  const [weightData, setWeightData] = useState<{ starting: number; current: number; goal: number } | null>(null);
  const [loadingWeight, setLoadingWeight] = useState(true);
  const [clientName, setClientName] = useState<string>("User");

  // Check if user needs onboarding tour
  useEffect(() => {
    (async () => {
      const user = await currentUser();
      if (!user) return;

      const { data } = await getSupabase()
        .from("clients")
        .select("onboarding_completed")
        .eq("user_id", user.id)
        .single();

      if (!data?.onboarding_completed) {
        setShowTour(true);
      }
    })();
  }, []);

  const handleTourComplete = async () => {
    setShowTour(false);
    
    const user = await currentUser();
    if (!user) return;

    await getSupabase()
      .from("clients")
      .update({ onboarding_completed: true })
      .eq("user_id", user.id);
  };

  const handleTourSkip = async () => {
    setShowTour(false);
    
    const user = await currentUser();
    if (!user) return;

    await getSupabase()
      .from("clients")
      .update({ onboarding_completed: true })
      .eq("user_id", user.id);
  };

  // Load all data
  useEffect(() => {
    (async () => {
      try {
        await Promise.all([
          loadWeeklyMacros().then(({ weekData: data, today }) => {
            setWeekData(data);
            setTodayGoals(today);
            setLoadingMacros(false);
          }),
          currentUser().then(async (user) => {
            if (!user) return;
            const { data } = await getSupabase().from("clients").select("name").eq("user_id", user.id).single();
            if (data?.name) setClientName(data.name.trim());
          }),
          loadRecentTraining().then((logs) => {
            setTrainingData(logs);
            setLoadingTraining(false);
          }),
          loadWeightData().then((data) => {
            setWeightData(data);
            setLoadingWeight(false);
          }),
        ]);
        
        setDataLoaded(true);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        setDataLoaded(true);
      }
    })();
  }, []);

// Calculate current week's date range (Monday to Sunday)
  const getCurrentWeekRange = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    
    // Calculate Monday (start of week)
    const monday = new Date(now);
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    monday.setDate(now.getDate() - daysFromMonday);
    
    // Calculate Sunday (end of week)
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const formatDate = (date: Date) => 
      date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    
    return `${formatDate(monday)} – ${formatDate(sunday)}`;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    return hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  };

  if (isLoading && !dataLoaded) {
    return <DashboardLoader onComplete={() => setIsLoading(false)} />;
  }

  return (
    <>
      {showTour && (
        <ProductTour 
          onComplete={handleTourComplete}
          onSkip={handleTourSkip}
        />
      )}

      <div className="flex h-screen bg-[#ffffff] text-[#0f172a] overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          {/* Header - stacks on mobile */}
          <header className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold truncate">
                {getGreeting()}, <span className="text-green-400">{clientName}</span>
              </h1>
              <p className="text-gray-500 mt-1 text-sm md:text-base">Your weekly performance at a glance</p>
            </div>
            {/* Action buttons - wrap on mobile */}
            <div className="flex flex-wrap gap-2 md:gap-3 w-full sm:w-auto">
              <button
                onClick={openAddActivity}
                className="flex-1 sm:flex-none px-3 md:px-5 py-2 md:py-3 bg-green-500 text-[#0f172a] text-sm md:text-base font-medium rounded-xl hover:bg-green-400 transition whitespace-nowrap"
              >
                + Activity
              </button>
              <button
                onClick={openLogFood}
                className="flex-1 sm:flex-none px-3 md:px-5 py-2 md:py-3 bg-green-500 text-[#0f172a] text-sm md:text-base font-medium rounded-xl hover:bg-green-400 transition whitespace-nowrap"
              >
                + Food
              </button>
              <button
                onClick={openUpdateGoal}
                className="flex-1 sm:flex-none px-3 md:px-5 py-2 md:py-3 bg-gray-700 text-[#0f172a] text-sm md:text-base font-medium rounded-xl hover:bg-gray-600 transition whitespace-nowrap"
              >
                Update Goal
              </button>
            </div>
          </header>

         {/* Date & Weight Section */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Calendar className="w-4 h-4" />
              <span>{getCurrentWeekRange()}</span>
            </div>

            {/* Weight cards - horizontal scroll on mobile */}
            <div className="flex gap-2 md:gap-4 overflow-x-auto pb-2 w-full lg:w-auto">
              {loadingWeight ? (
                <span className="text-gray-500">Loading...</span>
              ) : weightData ? (
                <>
                  <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-xl md:rounded-2xl px-3 md:px-6 py-2 md:py-4 text-center min-w-[90px] md:min-w-[120px] flex-shrink-0">
                    <p className="text-[10px] md:text-xs text-gray-500">Starting</p>
                    <p className="text-lg md:text-2xl font-bold text-[#0f172a]">{weightData.starting}kg</p>
                  </div>
                  <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-xl md:rounded-2xl px-3 md:px-6 py-2 md:py-4 text-center min-w-[90px] md:min-w-[120px] relative flex-shrink-0">
                    <p className="text-[10px] md:text-xs text-gray-500">Current</p>
                    <p className="text-lg md:text-2xl font-bold text-[#0f172a]">{weightData.current}kg</p>
                    {weightData.starting !== weightData.current && (
                      <span className="absolute -top-1 -right-1 md:-top-2 md:-right-2 bg-red-500 text-[#0f172a] text-[10px] md:text-xs font-medium px-1.5 md:px-2 py-0.5 rounded-full">
                        {weightData.current < weightData.starting
                          ? `-${Math.round(((weightData.starting - weightData.current) / weightData.starting) * 100)}%`
                          : `+${Math.round(((weightData.current - weightData.starting) / weightData.starting) * 100)}%`}
                      </span>
                    )}
                  </div>
                  <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-xl md:rounded-2xl px-3 md:px-6 py-2 md:py-4 text-center min-w-[90px] md:min-w-[120px] flex-shrink-0">
                    <p className="text-[10px] md:text-xs text-gray-500">Goal</p>
                    <p className="text-lg md:text-2xl font-bold text-[#0f172a]">{weightData.goal}kg</p>
                  </div>
                </>
              ) : (
                <span className="text-gray-500">No weight data</span>
              )}
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-8">
            {/* Macro Cards - 2x2 grid */}
            <div className="lg:col-span-8">
              <div className="grid grid-cols-2 gap-2 md:gap-4 h-full">
                <MacroCard title="Calories" current={weekData?.[6]?.cal?.toString() ?? "0"} goal={todayGoals.cal.toString()} dataKey="cal" weekData={weekData} loadingMacros={loadingMacros} />
                <MacroCard title="Protein (g)" current={weekData?.[6]?.pro?.toString() ?? "0"} goal={todayGoals.pro.toString()} dataKey="pro" weekData={weekData} loadingMacros={loadingMacros} />
                <MacroCard title="Carbs (g)" current={weekData?.[6]?.carb?.toString() ?? "0"} goal={todayGoals.carb.toString()} dataKey="carb" weekData={weekData} loadingMacros={loadingMacros} />
                <MacroCard title="Fat (g)" current={weekData?.[6]?.fat?.toString() ?? "0"} goal={todayGoals.fat.toString()} dataKey="fat" weekData={weekData} loadingMacros={loadingMacros} />
              </div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-4 space-y-4 md:space-y-6">
              
              {/* ——————————————————————————————————
                  NEW: Single component replaces 3 referral cards + ~100 lines of code
                  ——————————————————————————————————  */}
              <ReferralPanelCompact />

              {/* Training Summary Card */}
              <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-base md:text-lg font-semibold">Training Summary</h2>
                  <Link 
                    to="/training"
                    className="text-xs text-green-400 hover:text-green-300 transition font-medium flex items-center gap-1"
                  >
                    View All
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>

                {/* Weekly Stats */}
                {!loadingTraining && trainingData.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4 pb-4 border-b border-[#e2e8f0]">
                    <div className="text-center">
                      <p className="text-lg md:text-2xl font-bold text-green-400">
                        {trainingData.length}
                      </p>
                      <p className="text-[10px] md:text-xs text-gray-500">Workouts</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg md:text-2xl font-bold text-green-400">
                        {trainingData.reduce((sum, t) => sum + (t.calories_burned || 0), 0).toLocaleString()}
                      </p>
                      <p className="text-[10px] md:text-xs text-gray-500">Total Cals</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg md:text-2xl font-bold text-green-400">
                        {trainingData.length > 0 ? Math.round(
  trainingData.reduce((sum, t) => sum + (t.avg_heart_rate || 0), 0) / trainingData.length
) : 0}
                      </p>
                      <p className="text-[10px] md:text-xs text-gray-500">Avg HR</p>
                    </div>
                  </div>
                )}

                {/* Training List */}
                <div className="space-y-2 md:space-y-3 max-h-48 md:max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                  {loadingTraining ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-3 animate-pulse">
                          <div className="w-8 h-8 md:w-9 md:h-9 rounded bg-gray-800" />
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-gray-800 rounded w-3/4" />
                            <div className="h-3 bg-gray-800 rounded w-1/2" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : trainingData.length === 0 ? (
                    <div className="text-center py-6 md:py-8">
                      <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 rounded-full bg-gray-800/50 flex items-center justify-center">
                        <Dumbbell className="w-6 h-6 md:w-8 md:h-8 text-gray-600" />
                      </div>
                      <p className="text-gray-500 text-sm mb-2">No recent sessions</p>
                      <button
                        onClick={openAddActivity}
                        className="text-xs text-green-400 hover:text-green-300 transition font-medium"
                      >
                        Log your first workout →
                      </button>
                    </div>
                  ) : (
                    trainingData.map((t, i) => (
                      <div 
                        key={i} 
                        className="flex items-center justify-between text-sm p-2 md:p-3 rounded-xl hover:bg-[#f1f5f9] transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center gap-2 md:gap-3 min-w-0">
                          <div className="w-8 h-8 md:w-9 md:h-9 rounded flex items-center justify-center overflow-hidden bg-black/50 group-hover:scale-110 transition-transform flex-shrink-0">
                            {t.source === "garmin" && (
                              <img src="https://static.garmincdn.com/com.garmin.connect/content/images/developer/gc-app-tile/xxxhdpi/gc-app-tile_@480.png" alt="Garmin" className="w-full h-full object-contain" />
                            )}
                            {t.source === "whoop" && (
                              <img src="https://usbamprqshmoalvlkjch.supabase.co/storage/v1/object/public/branding/WHOOPWHITELOGO2.png" alt="WHOOP" className="w-full h-full object-contain px-1" />
                            )}
                            {(!t.source || (t.source !== "garmin" && t.source !== "whoop")) && (
                              <div className="w-full h-full rounded bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-xs font-bold text-[#0f172a]">
                                {t.activity_type?.[0]?.toUpperCase() || "?"}
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-600 capitalize group-hover:text-[#0f172a] transition-colors text-xs md:text-sm truncate">
                              {t.activity_type || "unknown"}
                            </p>
                            <p className="text-[10px] md:text-xs text-gray-500">
                              {new Date(t.created_at).toLocaleDateString("en-GB", { 
                                day: "numeric", 
                                month: "short",
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <p className="text-green-400 font-medium text-[10px] md:text-xs">
                            {t.avg_heart_rate ? `${t.avg_heart_rate} bpm` : "--"}
                          </p>
                          <p className="text-[10px] md:text-xs text-gray-500">
                            {t.calories_burned ? `${t.calories_burned} Cal` : "--"}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Custom Scrollbar Styles */}
              <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                  width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                  background: #f1f5f9;
                  border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                  background: #a78bfa;
                  border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                  background: #8b5cf6;
                }
              `}</style>
              
            </div>
          </div>

          <footer className="mt-8 md:mt-12 text-center text-gray-500 text-xs">
            © {new Date().getFullYear()} Input
          </footer>
        </main>
      </div>
    </>
  );
}
